import React, { useEffect, useCallback, ReactNode, useRef } from 'react';
import { observer } from 'mobx-react';
import { useMediaQuery, useTheme } from '@mui/material';

import SidebarDelimiter from '../../SidebarDelimiter';
import { useAppStore } from '../../../hooks/useAppStore';
import { SIDEBAR_MIN_WIDTH } from '../../../types/sidebar';

interface ResizableDrawerWrapperProps {
    children: ReactNode;
}

const ResizableDrawerWrapper: React.FC<ResizableDrawerWrapperProps> = observer(({ children }) => {
    const { isCollapsed, setSidebarWidth, setCollapsed, isResizing, setIsResizing } = useAppStore();
    const isCollapsedRef = useRef(isCollapsed);
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Keep ref in sync with store value
    useEffect(() => {
        isCollapsedRef.current = isCollapsed;
    }, [isCollapsed]);


    // Resize handlers
    const handleResizeStart = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        setIsResizing(true);
    }, []);

    useEffect(() => {
        if (!isResizing) return;

        const handleMouseMove = (e: MouseEvent) => {
            if (isResizing) {
                const newWidth = e.clientX;
                setSidebarWidth(newWidth);
                
                // Update isCollapsed based on width
                // If width is at or below minimum, collapse; otherwise expand
                const shouldBeCollapsed = newWidth <= SIDEBAR_MIN_WIDTH;
                if (shouldBeCollapsed !== isCollapsedRef.current) {
                    setCollapsed(shouldBeCollapsed);
                    isCollapsedRef.current = shouldBeCollapsed;
                }
            }
        };

        const handleMouseUp = () => {
            setIsResizing(false);
        };

        if(!isMobile){
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            if(!isMobile){
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            }
        };
    }, [isResizing, setSidebarWidth, setCollapsed, isMobile]);

    return (
        <>
            {children}
            {
                isMobile ? null: 
                    <SidebarDelimiter
                        onResizeStart={handleResizeStart}
                        isResizing={isResizing}
                    />
            }
        </>
    );
});

export default ResizableDrawerWrapper;

